export type TimedValue = {
  position: number;
  value: number;
};

/**
 * A centered simple moving average for category-like series (day, month,
 * weekday). Invalid values are ignored instead of poisoning the whole window.
 */
export function centeredMovingAverage(values: number[], windowSize = 5) {
  if (!values.length) return [];
  const normalizedWindow = Math.max(1, Math.floor(windowSize));
  const radius = Math.floor(normalizedWindow / 2);

  return values.map((_, index) => {
    const start = Math.max(0, index - radius);
    const end = Math.min(values.length, index + radius + 1);
    let sum = 0;
    let count = 0;
    for (let cursor = start; cursor < end; cursor += 1) {
      const value = values[cursor];
      if (!Number.isFinite(value)) continue;
      sum += value;
      count += 1;
    }
    return count ? sum / count : 0;
  });
}

/**
 * Smooth time-of-day data without letting duplicate samples from one clock
 * time create several different trend points. Samples are first averaged into
 * fixed time buckets, then a triangular kernel is applied over actual minutes.
 * The result is mapped back to every original sample so hover and raw points
 * remain intact while the trend is continuous across days.
 */
export function timeAwareMovingAverage(
  samples: TimedValue[],
  windowMinutes = 40,
  bucketMinutes = 10,
) {
  if (!samples.length) return [];
  const safeBucketMinutes = Math.max(1, bucketMinutes);
  const safeWindowMinutes = Math.max(safeBucketMinutes, windowMinutes);
  const buckets = new Map<number, { sum: number; count: number }>();

  samples.forEach(({ position, value }) => {
    if (!Number.isFinite(position) || !Number.isFinite(value)) return;
    const bucketStart = Math.floor(position / safeBucketMinutes) * safeBucketMinutes;
    const current = buckets.get(bucketStart) ?? { sum: 0, count: 0 };
    current.sum += value;
    current.count += 1;
    buckets.set(bucketStart, current);
  });

  const bucketValues = [...buckets.entries()]
    .map(([start, bucket]) => ({
      position: start + safeBucketMinutes / 2,
      value: bucket.sum / Math.max(bucket.count, 1),
    }))
    .sort((left, right) => left.position - right.position);

  if (!bucketValues.length) {
    return samples.map(({ value }) => (Number.isFinite(value) ? value : 0));
  }

  return samples.map((sample) => {
    if (!Number.isFinite(sample.position)) return Number.isFinite(sample.value) ? sample.value : 0;
    let weightedSum = 0;
    let totalWeight = 0;
    bucketValues.forEach((bucket) => {
      const distance = Math.abs(bucket.position - sample.position);
      if (distance > safeWindowMinutes) return;
      const weight = 1 - distance / (safeWindowMinutes + safeBucketMinutes);
      weightedSum += bucket.value * weight;
      totalWeight += weight;
    });
    return totalWeight ? weightedSum / totalWeight : sample.value;
  });
}
